import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import styled from 'styled-components';

import {
  ManagerProgressionTaskState,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import { Collection } from '@sorare/core/src/atoms/icons/Collection';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import {
  BodyS,
  BrandXS,
  LabelL,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { LightedBezel } from '@sorare/core/src/atoms/ui/bezels/LightedBezel';
import {
  AnyRewardConfig,
  Variant,
} from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { isType } from '@sorare/core/src/lib/gql';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CardCollectionDetailsDialog } from '../CardCollectionTask/CardCollectionDetailsDialog';
import { CollectionCompletionDetailsDialog } from '../CollectionCompletionTask/CollectionCompletionDetailsDialog';
import { ClaimButton } from '../UnclaimedTask/ClaimButton';
import type { SetCollectionTask_taskInterface } from './__generated__/index.graphql';

const Container = styled.div`
  width: 100%;
  position: relative;
  &.claimed {
    opacity: 0.5;
  }
`;

const CardContent = styled(Horizontal).attrs({ gap: 2 })`
  width: 100%;
  padding: var(--double-unit);
  position: relative;
  @media ${laptopAndAbove} {
    padding: var(--triple-unit);
  }
`;

const StyledLightedBezel = styled(LightedBezel)`
  width: 100%;
`;

const StyledLinkOverlay = styled(ButtonBase)`
  width: 100%;
  text-align: left;
  cursor: pointer;
`;

const IncompleteCardContainer = styled(ButtonBase)`
  width: 100%;
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--unit);
  background: var(--c-nl-200);
  text-align: left;
  position: relative;
`;

const ProgressSection = styled(Horizontal).attrs({ gap: 1 })`
  flex: 1;
  align-items: center;
  min-width: 0;
`;

const StyledProgress = styled(Progress)`
  flex: 1;
  min-width: 0;
`;

const ChevronWrapper = styled(Horizontal).attrs({ gap: 0 })`
  position: absolute;
  right: var(--double-unit);
  top: 50%;
  transform: translateY(-50%);
`;

const ContentWrapper = styled(Vertical)`
  @media ${laptopAndAbove} {
    align-items: flex-start;
  }
`;

type Props = {
  task: SetCollectionTask_taskInterface;
  onClaim: () => void;
  loading: boolean;
};

export const SetCollectionTask = ({ task, onClaim, loading }: Props) => {
  const [dialogOpened, setDialogOpened] = useState(false);

  if (
    !isType(task, 'CardsCountTask') &&
    !isType(task, 'CompleteCollectionsCountTask')
  ) {
    return null;
  }

  const { title, description, rewardConfigs, aasmState } = task;

  const rewardConfig = rewardConfigs.at(0);
  const isCompleted = aasmState === ManagerProgressionTaskState.COMPLETED;
  const isClaimed = aasmState === ManagerProgressionTaskState.CLAIMED;

  // Extract league name from description or use a default
  // This is a placeholder - you may need to adjust based on actual data structure
  const leagueName = 'WNTR'; // TODO: Extract from task data if available

  const getDialog = () => {
    if (isType(task, 'CardsCountTask')) {
      return (
        <CardCollectionDetailsDialog
          open={dialogOpened}
          onClose={() => setDialogOpened(false)}
          task={task}
        />
      );
    }
    if (isType(task, 'CompleteCollectionsCountTask')) {
      return (
        <CollectionCompletionDetailsDialog
          open={dialogOpened}
          onClose={() => setDialogOpened(false)}
          task={task}
        />
      );
    }
    return null;
  };

  if (!isCompleted && !isClaimed) {
    const { liveScore, scoreToReach = 1 } = task;

    return (
      <Container>
        <IncompleteCardContainer onClick={() => setDialogOpened(true)}>
          <Vertical>
            <Vertical>
              <BrandXS bold color="var(--c-wntr-series)">
                {leagueName}
              </BrandXS>
              <LabelL bold>{title}</LabelL>
              <BodyS>{description}</BodyS>
            </Vertical>
            <SBHorizontal gap={2}>
              <ProgressSection>
                <StyledProgress
                  value={liveScore}
                  max={scoreToReach}
                  color="var(--c-wntr-series)"
                  bgColor="var(--c-nd-150-opaque)"
                />
                <Horizontal>
                  <LabelS bold>{liveScore}</LabelS>
                  <LabelS color="var(--c-nd-600)">/</LabelS>
                  <LabelS color="var(--c-nd-600)" bold>
                    {scoreToReach}
                  </LabelS>
                  {isType(task, 'CardsCountTask') ? (
                    <ScarcityIcon
                      rarity={Rarity.common}
                      // TODO: Get this from backend (CardsCountTask.cardSet ?)
                      cardSet="winter"
                    />
                  ) : (
                    <Collection size={2} />
                  )}
                </Horizontal>
              </ProgressSection>
              {!!rewardConfig && (
                <AnyRewardConfig
                  anyRewardConfig={rewardConfig}
                  variant={Variant.INLINE_CHIP}
                />
              )}
            </SBHorizontal>
          </Vertical>
          <ChevronWrapper>
            <ChevronRightBold />
          </ChevronWrapper>
        </IncompleteCardContainer>
        {getDialog()}
      </Container>
    );
  }

  return (
    <Container className={classNames({ claimed: isClaimed })}>
      <StyledLightedBezel
        color="var(--c-wntr-series)"
        bezelSize={16}
        radius={16}
        borderWidth={1}
        background="conic-gradient(from 90deg at 3px 3px, var(--c-nd-100) 25%, rgba(var(--c-rgb-black), 0.2) 0) 0 0/5px 5px"
        boxShadow={
          isClaimed
            ? undefined
            : '0px 0px 8px 2px rgba(23, 158, 255, 0.2) inset'
        }
      >
        <CardContent>
          {!!rewardConfig && (
            <AnyRewardConfig
              anyRewardConfig={rewardConfig}
              variant={Variant.CHIP}
            />
          )}
          <ContentWrapper>
            <BrandXS bold color="var(--c-wntr-series)">
              {leagueName}
            </BrandXS>
            <StyledLinkOverlay onClick={() => setDialogOpened(true)}>
              <LabelL bold>{title}</LabelL>
              <BodyS>{description}</BodyS>
            </StyledLinkOverlay>
            {!!isCompleted && (
              <ClaimButton task={task} onClaim={onClaim} loading={loading} />
            )}
          </ContentWrapper>
        </CardContent>
      </StyledLightedBezel>
      {getDialog()}
    </Container>
  );
};

SetCollectionTask.fragments = {
  taskInterface: gql`
    fragment SetCollectionTask_taskInterface on TaskInterface {
      id
      aasmState
      title
      description

      iconUrl
      rewardConfigs {
        id
        ...AnyRewardConfig_anyRewardConfigInterface
      }
      ... on CompleteCollectionsCountTask {
        id
        liveScore
        scoreToReach
        ...CollectionCompletionDetailsDialog_taskInterface
      }
      ... on CardsCountTask {
        id
        liveScore
        scoreToReach
        ...CardCollectionDetailsDialog_taskInterface
      }
      ...ClaimButton_taskInterface
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
    ${CardCollectionDetailsDialog.fragments.taskInterface}
    ${CollectionCompletionDetailsDialog.fragments.taskInterface}
    ${ClaimButton.fragments.taskInterface}
  ` as TypedDocumentNode<SetCollectionTask_taskInterface>,
};
